import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { REPLENISHMENT_PAYMENT_STATUS, paymentStatusOptions, getUnitById } from '../../utils/Enums';
import { formatDateTime } from '../../utils/DateUtils';

import { formatMoney } from '../../utils/currency';
const ViewReplenishmentDetailsModal = ({ open, onClose, replenishment }) => {
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = useState(0);

  const handleChangeTab = (event, newValue) => {
    setTabIndex(newValue);
  };

  const expirationMessage = (replenishment) => {
    if (replenishment.paymentStatus !== REPLENISHMENT_PAYMENT_STATUS.PENDING) {
      return null; // No expiration message needed if payment status is not pending
    }

    if (replenishment.expirationDays < 0) {
      return (
        <span className="roboto-light" style={{ color: 'red' }}>
          {t('expired', { days: Math.abs(replenishment.expirationDays) })}
        </span>
      );
    }
    
    return <span className="roboto-light">{`${t('days_to_expire', { days: replenishment.expirationDays })}`}</span>;
  };

  if (!replenishment) { return;}
 
  const message = expirationMessage(replenishment);
  const supplierDetails = replenishment.supplierDetails || null;
  const hasNormalizedSupplierDetails = Boolean(supplierDetails);
  const getSupplierValue = (normalizedValue, legacyValue, allowZero = false) => {
    if (hasNormalizedSupplierDetails) {
      return allowZero ? (normalizedValue ?? '-') : (normalizedValue || '-');
    }
    return allowZero ? (legacyValue ?? '-') : (legacyValue || '-');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('replenishment_details')}</DialogTitle>
      <DialogContent>
        <Tabs value={tabIndex} onChange={handleChangeTab} aria-label="detail tabs">
          <Tab label={t('billing')} />
          <Tab label={t('supplier')} />
          <Tab label={t('products')} />
        </Tabs>
        {tabIndex === 0 && (
          <Box p={3}>
            <Typography><span className="roboto-regular">{t('date')}:</span> <span className="roboto-light">{formatDateTime(replenishment.date)}</span></Typography>
            <Typography><span className="roboto-regular">{t('total_price')}:</span> <span className="roboto-light">{formatMoney(replenishment.totalPrice, replenishment.currency)}</span></Typography>
            <Typography><span className="roboto-regular">{t('payment_status')}:</span> <span className="roboto-light">{t(paymentStatusOptions.find(status => status.id === replenishment.paymentStatus)?.label)}</span></Typography>
            <Typography><span className="roboto-regular">{t('expiration')}:</span> {message && <>{message}</>}</Typography>
            <Typography><span className="roboto-light">{replenishment.invoiceFile && <a href={replenishment.invoiceFile} target="_blank" rel="noopener noreferrer">{t('view_invoice')}</a>}</span></Typography>
          </Box>
        )}
        {tabIndex === 1 && (
          <Box p={3}>
            <Typography><span className="roboto-regular">{t('business_name')}:</span> <span className="roboto-light">{supplierDetails?.displayName || replenishment.supplierDisplayName || replenishment.supplier?.businessName || replenishment.supplier?.name || t('no_supplier')}</span></Typography>
            <Typography><span className="roboto-regular">{t('rut')}:</span> <span className="roboto-light">{getSupplierValue(supplierDetails?.rut, replenishment.supplier?.rut)}</span></Typography>
            <Typography><span className="roboto-regular">{t('address')}:</span> <span className="roboto-light">{getSupplierValue(supplierDetails?.address, replenishment.supplier?.pickupAddress)}</span></Typography>
            <Typography><span className="roboto-regular">{t('phone')}:</span> <span className="roboto-light">{getSupplierValue(supplierDetails?.phone, replenishment.supplier?.businessPhone)}</span></Typography>
            <Typography><span className="roboto-regular">{t('email')}:</span> <span className="roboto-light">{getSupplierValue(supplierDetails?.email, replenishment.supplier?.email)}</span></Typography>
            <br/>
            <Typography><span className="roboto-regular">{t('seller_name')}:</span> <span className="roboto-light">{getSupplierValue(supplierDetails?.sellerName, replenishment.supplier?.sellerName)}</span></Typography>
            <Typography><span className="roboto-regular">{t('seller_phone')}:</span> <span className="roboto-light">{getSupplierValue(supplierDetails?.sellerPhone, replenishment.supplier?.sellerPhone)}</span></Typography>
            <br/>
            <Typography><span className="roboto-regular">{t('bank')}:</span> <span className="roboto-light">{getSupplierValue(supplierDetails?.bankLabel, replenishment.supplier?.bank?.label)}</span></Typography>
            <Typography><span className="roboto-regular">{t('bank_account')}:</span> <span className="roboto-light">{getSupplierValue(supplierDetails?.bankAccount, replenishment.supplier?.bankAccount)}</span></Typography>
            <br/>
            <Typography><span className="roboto-regular">{t('credit_days')}:</span> <span className="roboto-light">{getSupplierValue(supplierDetails?.creditDays, replenishment.supplier?.creditDays, true)}</span></Typography>
            <Typography><span className="roboto-regular">{t('anticipation_days')}:</span> <span className="roboto-light">{getSupplierValue(supplierDetails?.anticipationDays, replenishment.supplier?.anticipationDays, true)}</span></Typography>
            <Typography><span className="roboto-regular">{t('delivery_days')}:</span> <span className="roboto-light">{getSupplierValue(supplierDetails?.deliveryDays, replenishment.supplier?.deliveryDays)}</span></Typography>
          </Box>
        )}
        {tabIndex === 2 && (
          <Box p={3}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '25%' }}><span className="roboto-regular">{t('item') || t('product')}</span></TableCell>
                    <TableCell align="center"><span className="roboto-regular">{t('quantity')}</span></TableCell>
                    <TableCell align="center"><span className="roboto-regular">{t('unit_price')}</span></TableCell>
                    <TableCell align="center"><span className="roboto-regular">{t('lot')}</span></TableCell>
                    <TableCell align="right"><span className="roboto-regular">{t('expiration_date')}</span></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {replenishment.items.map((item, index) => {
                    // Determinar el nombre según el tipo de referencia
                    const itemName = item.supply?.name || item.product?.name || t('unknown_item');
                    const referenceType = item.referenceType?.value || item.referenceType;
                    const isProduct = referenceType === 4 || (item.product && !item.supply); // 4 = PRODUCT
                    const unitId = item.supply?.unit ?? item.product?.unit;
                    const unitLabel = (unitId != null && unitId !== undefined) ? getUnitById(unitId).label : '';
                    const quantityDisplay = unitLabel ? `${item.quantity} ${unitLabel}` : item.quantity;
                    
                    return (
                      <TableRow key={index}>
                        <TableCell><span className="roboto-light">{itemName}</span></TableCell>
                        <TableCell align="center"><span className="roboto-light">{quantityDisplay}</span></TableCell>
                        <TableCell align="center"><span className="roboto-light">{formatMoney(item.unitPrice, replenishment.currency)}</span></TableCell>
                        <TableCell align="center"><span className="roboto-light">{item.lot || '-'}</span></TableCell>
                        <TableCell align="right">
                          <span className="roboto-light">
                            {item.expirationDate ? new Date(item.expirationDate).toLocaleDateString() : t('no_expiration')}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="subtitle1" align="right" style={{ marginTop: '10px' }}>
              <span className="roboto-light">{t('total_cost')}: {formatMoney(replenishment.items.reduce((acc, item) => acc + (item.quantity * item.unitPrice), 0), replenishment.currency)}</span>
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewReplenishmentDetailsModal;
